import { z } from 'zod';
import { AddressSchema } from './address';
import { UUIDSchema } from './common';
import { EnergyEfficiencyClassSchema } from './energy';
import { AreaValueSchema } from './measurements';
const OwnerSchema = z.union([
    z.object({ owner_type: z.literal('legal_entity'), name: z.string() }),
    z.object({
        owner_type: z.literal('physical_person'),
        name: z.object({ first_name: z.string(), last_name: z.string(), salutation: z.string().optional() }),
    }),
    z.object({
        owner_type: z.literal('ownership_group'),
        owners: z.array(z.object({
            ownership_ratio: z.object({ numerator: z.number(), denominator: z.number() }),
            owner: z.union([
                z.object({ owner_type: z.literal('legal_entity'), name: z.string() }),
                z.object({
                    owner_type: z.literal('physical_person'),
                    name: z.object({ first_name: z.string(), last_name: z.string(), salutation: z.string().optional() }),
                }),
            ]),
        })),
    }),
]);
const LegalPlotBaseSchema = z.object({
    sequence_number: z.string().optional(),
    district: z.string().optional(),
    field_map: z.string().optional(),
    plot_number: z.string(),
    land_use_description: z.string().optional(),
    area: AreaValueSchema,
});
const LegalPlotSchema = z.discriminatedUnion('plot_type', [
    LegalPlotBaseSchema.extend({ plot_type: z.literal('solely_owned') }),
    LegalPlotBaseSchema.extend({
        plot_type: z.literal('co_owned'),
        denominator: z.number(),
        shares: z.array(z.object({ numerator: z.number(), title: z.string() })),
    }),
]);
const LandRegistrySheetSchema = z.object({
    uuid: UUIDSchema.optional(),
    sheet_number: z.string(),
    owner: OwnerSchema.optional(),
    local_court: z.string().optional(),
    district: z.string().optional(),
    legal_date: z.string().optional(),
    legal_date_text: z.string().optional(),
    volume: z.string().optional(),
    plots: LegalPlotSchema.array().optional(),
    encumbrances: z
        .array(z.object({
        description: z.string(),
        note: z.string(),
        sequence_number: z.string(),
        plot_indexes: z.number().array(),
        encumbrance_type: z.enum([
            'common_encumbrance',
            'leasehold',
            'residential_right',
            'usufruct',
            'right_of_first_refusal',
            'limited_personal_servitude',
            'servitude',
        ]),
    }))
        .optional(),
    inventory_register_note: z.string().optional(),
    dominance_notations: z
        .array(z.object({ description: z.string(), sequence_number: z.string(), plot_indexes: z.number().array() }))
        .optional(),
    owner_section_description: z.string().optional(),
    encumbrances_section_description: z.string().optional(),
});
const LandConditionSchema = z.object({
    property_shape: z.string(),
    zoning_plan: z.string(),
    topography: z.string(),
    access: z.string(),
    road_type: z.string(),
    road_expansion: z.string(),
    technical_infrastructure: z.string().array(),
    taxation_status: z.string(),
    land_quality_according_to_regulation: z.string(),
    others: z.string().array(),
});
const ExteriorSchema = z.object({
    exterior_surfaces: z.string().array().optional(),
    plantings: z.string().array().optional(),
    fencing: z.string().array().optional(),
    others: z.string().array().optional(),
    description: z.string().optional(),
});
const AssetLandShape = {
    area: AreaValueSchema,
    land_registry_sheets: LandRegistrySheetSchema.array(),
    land_condition: LandConditionSchema,
    exterior: ExteriorSchema,
    land_registry_text: z.string().optional(),
};
export const AssetLandSchema = z.object(AssetLandShape);
const ConstructionMethodSchema = z.object({
    material: z
        .enum([
        'timber_construction',
        'masonry_construction',
        'mixed_construction',
        'reinforced_concrete_construction',
        'glass_construction',
    ])
        .optional(),
    weight_of_construction: z.enum(['lightweight_construction', 'solid_construction']).optional(),
    assembly_of_components: z.enum(['prefabricated_construction', 'large_panel_construction', 'modular_construction']).optional(),
    supporting_structure: z
        .enum([
        'skeleton_construction',
        'bulkhead_construction',
        'half_timbered_construction',
        'post_and_beam_construction',
        'tent_construction',
        'column_construction',
        'framing_construction',
    ])
        .optional(),
});
const StructureConditionSchema = z.object({
    structural_damage: z.enum(['none_apparently_present', 'present']).optional(),
    maintenance_backlog: z.enum(['none_present', 'present', 'very_minor', 'minor', 'major', 'extreme']).optional(),
    overall_condition_assessment: z
        .enum([
        'excellent',
        'very_good',
        'good',
        'above_average',
        'slightly_above_average',
        'age_appropriate',
        'average',
        'below_average',
        'mediocre',
        'bad',
        'catastrophic',
    ])
        .optional(),
    overall_equipment_assessment: z.enum(['above_average', 'average', 'below_average']).optional(),
    general_text: z.string().optional(),
});
const InteriorSchema = z.object({
    balconies_terraces: z.string().array().optional(),
    entrance_doors: z.string().array().optional(),
    floorings: z.string().array().optional(),
    interior_doors: z.string().array().optional(),
    interior_walls: z.string().array().optional(),
    sanitary_equipment: z.string().array().optional(),
    sun_protection: z.string().array().optional(),
    wall_coverings: z.string().array().optional(),
    windows: z.string().array().optional(),
    general_text: z.string().optional(),
});
const GarageInteriorSchema = z.object({
    floorings: z.string().array().optional(),
    ventilation: z.string().array().optional(),
    garage_gate: z.string().array().optional(),
    general_text: z.string().optional(),
});
const BuildingFacilitiesSchema = z.object({
    heating_concept: z.enum(['central_heating', 'floor_heating', 'individual_heating', 'individual_heaters']).optional(),
    type_of_heating: z.string().array().optional(),
    heat_output: z.string().array().optional(),
    fuel: z.string().array().optional(),
    heating_construction_year: z.string().optional(),
    ventilation_system: z
        .enum(['present', 'not_present', 'present_with_heat_recovery', 'present_in_some_areas', 'present_in_some_areas_with_heat_recovery'])
        .optional(),
    air_conditioning: z.enum(['present', 'not_present', 'present_in_some_areas']).optional(),
    hot_water_supply: z.enum(['centralized', 'decentralized', 'partly_centralized', 'partly_decentralized']).optional(),
    electrical_installation: z
        .enum([
        'flush_mounted',
        'surface_mounted',
        'window_sill_channels',
        'parapet_channels',
        'partly_flush_mounted',
        'partly_surface_mounted',
    ])
        .optional(),
    other_building_facilities: z
        .enum([
        'tiled_stove',
        'open_fireplace',
        'fireplace',
        'basic_stove',
        'photovoltaic_system',
        'photovoltaic_system_with_electricity_storage',
        'electric_underfloor_heating_in_some_areas',
    ])
        .array()
        .optional(),
    photovoltaics: z.enum(['not_present', 'present_owned', 'present_not_owned']).optional(),
    general_text: z.string().optional(),
});
const StructureBaseShape = {
    title: z.string(),
    uuid: UUIDSchema,
    construction_year: z.number().optional(),
    construction_method: ConstructionMethodSchema.optional(),
    structure_condition: StructureConditionSchema.optional(),
    energy_certificate_details: z
        .object({
        energy_efficiency_class: EnergyEfficiencyClassSchema.optional(),
        power_consumption: z.number().optional(),
        primary_power_consumption: z.number().optional(),
        certificate_type: z.enum(['requirements_depending', 'usage_depending', 'unknown']).optional(),
        carbon_dioxide_equivalent: z.number().optional(),
        usage_area: AreaValueSchema.optional(),
        issue_date: z.string().optional(),
        final_energy_consumption_electricity: z.number().optional(),
        final_energy_consumption_heat: z.number().optional(),
        modernization_recommendations: z.string().optional(),
        heating_energy_source: z.string().array().optional(),
        hot_water_energy_source: z.string().array().optional(),
        heat_generator_manufacture_year: z.number().optional(),
    })
        .optional(),
    modernization: z
        .object({
        modernization_degree: z.object({
            modernization_degree: z
                .enum([
                'no_information',
                'not_modernized',
                'small_modernization',
                'medium_modernization',
                'mostly_modernized',
                'fully_modernized',
            ])
                .optional(),
            detail_level: z.enum(['summary', 'detailed']),
            roofing: z.number().optional(),
            windows_and_exterior_doors: z.number().optional(),
            piping_wire_system: z.number().optional(),
            heating: z.number().optional(),
            insulation: z.number().optional(),
            bathroom: z.number().optional(),
            interior: z.number().optional(),
            floor_plan: z.number().optional(),
            general_text: z.string().optional(),
        }),
        modernization_measures: z.array(z.object({
            title: z.string(),
            description: z.string(),
            year: z.number().optional(),
            degree: z.number().optional(),
            year_text: z.string().optional(),
        })),
    })
        .optional(),
    gross_floor_area: AreaValueSchema.optional(),
    construction: z
        .object({
        roof_type: z.string().array().optional(),
        attic: z.string().optional(),
        elevator: z.string().array().optional(),
        floor_slabs: z.string().array().optional(),
        roof_covering: z.string().array().optional(),
        facade: z.string().array().optional(),
        staircase: z.string().array().optional(),
        exterior_wall: z.string().array().optional(),
        foundations: z.string().array().optional(),
        general_text: z.string().optional(),
    })
        .optional(),
    energy_certificate_description: z.string().optional(),
    sustainability_certificate_description: z.string().optional(),
    energy_assessment_description: z.string().optional(),
    floor_description: z.string().optional(),
    zoning_of_usage: z.string().optional(),
    building_access: z.string().optional(),
};
const StructureBaseSchema = z.object(StructureBaseShape);
const FloorStructureSchema = z.object({ full_floors: z.number(), total_floors: z.number() });
const ResidentialStructureSchema = StructureBaseSchema.extend({
    usage_type: z.literal('residential'),
    interior: InteriorSchema.optional(),
    floor_structure: FloorStructureSchema.optional(),
    building_facilities: BuildingFacilitiesSchema.optional(),
    construction_style: z.string().optional(),
    living_area: AreaValueSchema.optional(),
    usage_area: AreaValueSchema.optional(),
});
const RoofStructureSchema = z.object({
    is_flat: z.boolean(),
    attic_conversion_degree: z.number().nullable(),
    roof_type: z.object({ title: z.string(), code: z.string() }).optional(),
});
const CommercialStructureSchema = StructureBaseSchema.extend({
    interior: InteriorSchema.optional(),
    building_facilities: BuildingFacilitiesSchema.optional(),
    construction_style: z.string().optional(),
    usage_area: AreaValueSchema.optional(),
});
const ResidentialApartmentSchema = ResidentialStructureSchema.extend({ sub_usage_type: z.literal('apartment') });
const ResidentialApartmentBuildingSchema = ResidentialStructureSchema.extend({
    sub_usage_type: z.literal('apartment_building'),
    number_of_apartments: z.number().optional(),
    roof_structure: RoofStructureSchema.optional(),
    underground_conversion_degree: z.number().optional(),
});
const ResidentialOtherSchema = ResidentialStructureSchema.extend({
    sub_usage_type: z.enum([
        'single_family_house',
        'two_family_house',
        'semi_detached_house',
        'townhouse_middle',
        'townhouse_end',
        'mixed_use_apartment_building',
    ]),
    roof_structure: RoofStructureSchema.optional(),
    underground_conversion_degree: z.number().optional(),
});
const OfficeStructureSchema = CommercialStructureSchema.extend({ usage_type: z.literal('office'), sub_usage_type: z.literal('office') });
const RetailStructureSchema = CommercialStructureSchema.extend({
    usage_type: z.literal('retail'),
    sub_usage_type: z.enum(['consumer_market', 'department_store', 'car_dealer']),
});
const MixedStructureSchema = CommercialStructureSchema.extend({
    usage_type: z.literal('mixed'),
    sub_usage_type: z.literal('commercial_structure'),
    floor_structure: FloorStructureSchema.optional(),
    living_area: AreaValueSchema.optional(),
    number_of_apartments: z.number().optional(),
});
const GarageStructureSchema = StructureBaseSchema.extend({
    usage_type: z.literal('garage'),
    interior: GarageInteriorSchema.optional(),
    sub_usage_type: z.enum([
        'single_car_garage',
        'multi_car_garage',
        'multi_story_parking_garage',
        'underground_parking_garage',
        'commercial_vehicle_garage',
    ]),
    construction_style: z.string().optional(),
});
const OtherStructureSchema = StructureBaseSchema.extend({
    usage_type: z.literal('other'),
    sub_usage_type: z.literal('other'),
    interior: InteriorSchema.optional(),
    building_facilities: BuildingFacilitiesSchema.optional(),
    living_area: AreaValueSchema.optional(),
    usage_area: AreaValueSchema.optional(),
});
export const AssetStructureSchema = z.union([
    ResidentialApartmentSchema,
    ResidentialApartmentBuildingSchema,
    ResidentialOtherSchema,
    OfficeStructureSchema,
    RetailStructureSchema,
    MixedStructureSchema,
    GarageStructureSchema,
    OtherStructureSchema,
]);
const AssetEconomicUnitShape = {
    title: z.string(),
    asset_class: z.literal('residential'),
    structures: AssetStructureSchema.array(),
};
export const AssetEconomicUnitSchema = z.object(AssetEconomicUnitShape);
const AssetBaseShape = {
    title: z.string(),
    customer_uuid: UUIDSchema.optional(),
    customer_asset_reference: z.string().optional(),
    address: AddressSchema,
    land: AssetLandSchema.optional(),
    economic_units: AssetEconomicUnitSchema.array(),
    cost_center_uuid: UUIDSchema.optional(),
};
export const AssetBaseSchema = z.object(AssetBaseShape);
export const CreateAssetRequestSchema = AssetBaseSchema;
const ExistingAssetExtensionShape = {
    uuid: UUIDSchema,
    created_on: z.string(),
    updated_on: z.string(),
    created_by: z.number().optional(),
    has_leasehold: z.boolean().optional(),
    title_image: z.string().optional(),
    title_image_thumbnail: z.string().optional(),
    orders_number: z.number().optional(),
};
export const ExistingAssetSchema = AssetBaseSchema.extend(ExistingAssetExtensionShape);
const AssetListShape = { assets: ExistingAssetSchema.array() };
export const AssetListSchema = z.object(AssetListShape);
